import { Box, Button, Stack, Typography } from "@mui/material";
import Popover from "@mui/material/Popover";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink } from "../../../components/ExternalLink";
import { LinkButton } from "../../../components/LinkButton";
import { useHandleChase } from "../../../hooks/useHandleChase";
import { useCustomer } from "../../../hooks/useCustomer";
import { AdjustToneList } from "../../../components/AdjustToneList";
import { CustomerChaseSummaryModel, OrganisationFileResponse } from "@attelas/shared";
import { useSearchReceivables } from "../../../hooks/useSearchReceivables";
import { InvoiceSelect } from "../InvoiceSelect";
import { ChaseFooter } from "../ChaseInvoices/ChaseInvoicesFooter";
import { OrganisationFilePicker } from "../../../components/OrganisationFilePicker/OrganisationFilePicker";

type Props = {
  title: string;
  customerId: string | null | undefined;
  chasables: CustomerChaseSummaryModel;
  onSkip?: () => void;
  onPrevious?: () => void;
  groupedInvoices?: boolean;
  fullHeightInvoices?: boolean;
};

export function ChaseGroupedInvoicesContent({
  customerId,
  chasables,
  title,
  onSkip,
  onPrevious,
  groupedInvoices,
  fullHeightInvoices = false,
}: Props) {
  const navigate = useNavigate();

  const selectedInvoiceIds = React.useMemo(() => {
    return chasables.invoices
      .sort((invoiceA, invoiceB) => {
        return new Date(invoiceA.dueDate).getTime() - new Date(invoiceB.dueDate).getTime();
      })
      .map(({ id }) => id);
  }, [chasables.invoices]);

  const groupIdOfSelectedInvoices = React.useMemo(() => {
    const groupIds = chasables.invoices
      .filter(({ id }) => selectedInvoiceIds.includes(id))
      .map(({ groupId }) => groupId)
      .filter((groupId) => groupId !== null);
    const uniqueGroupIds = Array.from(new Set(groupIds));
    return uniqueGroupIds.length > 0 ? uniqueGroupIds[0] : undefined;
  }, [chasables.invoices, selectedInvoiceIds]);

  const { customer } = useCustomer(customerId);
  const { handleChase, handleDraftFromTemplate, addOtherContactsToCc, isLoading, hasBeenChased } = useHandleChase({
    customerId,
    invoiceIds: selectedInvoiceIds,
    contacts: customer?.contacts,
  });

  const [organisationFilePickerAnchor, setOrganisationFilePickerAnchor] = useState<HTMLElement | null>(null);
  const [adjustToneAnchor, setAdjustToneAnchor] = useState<HTMLElement | null>(null);

  const onComplete = async () => {
    onSkip?.();
  };

  const navigateToChasable = (event: React.MouseEvent<HTMLElement, MouseEvent>, invoiceId: string) => {
    event.preventDefault();

    if (invoiceId) {
      navigate(`/chasables/suggested/${invoiceId}?isFromSearch=true`);
    }
  };

  return (
    <>
      <Stack spacing={2} pb={12}>
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mx="-8px">
            {onPrevious && (
              <Button variant="text" onClick={onPrevious} sx={{ marginRight: -1 }}>
                Previous
              </Button>
            )}
            {onSkip && (
              <Button variant="text" onClick={onSkip} sx={{ marginRight: -1 }}>
                Next
              </Button>
            )}
          </Stack>
          <Typography variant="h2" sx={{ lineHeight: "18px", mb: 1 }}>
            {title}
          </Typography>
          <ExternalLink
            href={chasables.link}
          >{`${chasables.accountNumber ? `${chasables.accountNumber} | ` : ""}${chasables.companyName}`}</ExternalLink>
        </Box>
        <InvoiceSelect
          invoices={chasables.invoices ?? []}
          selectable={!groupedInvoices && !hasBeenChased}
          disabled={isLoading}
          selectedInvoiceIds={selectedInvoiceIds}
          onSelectChange={() => {}}
          onInvoiceClick={navigateToChasable}
          tableFooter={customerId && <LinkToSearch companyName={chasables.companyName} customerId={customerId} />}
          fullHeight={fullHeightInvoices}
        />
      </Stack>
      <OrganisationFilePicker
        open={!!organisationFilePickerAnchor}
        anchorEl={organisationFilePickerAnchor}
        onClose={() => setOrganisationFilePickerAnchor(null)}
        onAdd={(files) => {
          handleChase(undefined, files);
          setOrganisationFilePickerAnchor(null);
        }}
        alreadySelectedIds={[]}
      />
      <Popover
        open={!!adjustToneAnchor}
        anchorEl={adjustToneAnchor}
        onClose={() => setAdjustToneAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <AdjustToneList
          isLoading={isLoading}
          onSelectTone={(toneId) => {
            handleChase(toneId);
            setAdjustToneAnchor(null);
          }}
        />
      </Popover>
      <ChaseFooter
        selectedAll={true}
        completedInvoiceIds={[]}
        selectedInvoiceIds={selectedInvoiceIds}
        isChaseLoading={isLoading}
        hasBeenChased={hasBeenChased}
        onChase={(languageId, recipientMode) => handleChase(undefined, undefined, languageId, recipientMode)}
        onEmailTemplateClick={handleDraftFromTemplate}
        onComplete={onComplete}
        onSkip={onSkip}
        invoicesGroupId={groupIdOfSelectedInvoices}
        contacts={customer?.contacts}
        onOpenAttachPickerForDraft={(anchorEl) => setOrganisationFilePickerAnchor(anchorEl)}
        onOpenAdjustTone={hasBeenChased ? (anchorEl) => setAdjustToneAnchor(anchorEl) : undefined}
        onAddOtherContactsToCc={hasBeenChased ? addOtherContactsToCc : undefined}
      />
    </>
  );
}

export function LinkToSearch({ companyName, customerId }: { companyName: string; customerId: string }) {
  const { result, isLoading } = useSearchReceivables(companyName);
  const link = `/chasables/search?input=${companyName}&value=${customerId}&showBackButton=true`;

  if (result?.every(({ id }) => id !== customerId)) return null;

  return (
    <LinkButton href={link} disabled={isLoading}>
      See all invoices
    </LinkButton>
  );
}
